import { Injectable, inject, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';

import { NotesComponent } from '@features/notes';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subject, firstValueFrom } from 'rxjs';

import { ModalVariant, QuotationState } from '@shared-enums';

import { IQuotation } from 'src/app/@interfaces/quotation.interface';
import { QuotationsService } from 'src/app/@pages/quotations/quotations.service';
import { BillyInvoiceService } from 'src/app/@shared/components/card/billyInvoice.service';
import { DialogComponent } from 'src/app/@shared/components/dialog/dialog.component';
import { CookieAuthService } from 'src/app/@shared/storage-variables/cookie-auth.service';

import { AlertService } from '../alert/alert.service';
import { CompanyFeaturesService } from '../company-features/company-features.service';
import { DianInvoiceService } from '../dian/dian-invoice.service';
import { ModalService } from '../modal/modal.service';

@Injectable({
  providedIn: 'root',
})
export class SalesActionHandlerService implements OnDestroy {
  private quotationService = inject(QuotationsService);
  private spinnerService = inject(NgxSpinnerService);
  private cookieAuthService = inject(CookieAuthService);
  private billyInvoiceService = inject(BillyInvoiceService);
  private dianInvoiceService = inject(DianInvoiceService);
  private alertService = inject(AlertService);
  private translate = inject(TranslateService);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private modalService = inject(ModalService);
  private companyFeaturesService = inject(CompanyFeaturesService);

  private destroy$ = new Subject<void>();

  private actionCompletedSource = new Subject<string>();
  readonly actionCompleted$ = this.actionCompletedSource.asObservable();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.actionCompletedSource.complete();
  }

  private async confirmAction(
    message: string,
    title = 'Warning',
  ): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.alertService
        .open({
          title: this.translate.instant(title),
          message: this.translate.instant(message),
          type: 'warning',
          acceptText: this.translate.instant('Yes'),
          rejectText: this.translate.instant('Cancel'),
        })
        .subscribe({
          accept: () => resolve(true),
          reject: () => resolve(false),
          close: () => resolve(false),
        });
    });
  }

  private async showOperationResult(
    success: boolean,
    successMessage = 'Status has been changed succesfully.',
    errorMessage = 'error trying to update the quotation',
  ): Promise<void> {
    if (success) {
      await new Promise<void>((resolve) => {
        this.alertService
          .open({
            title: this.translate.instant('Success'),
            message: this.translate.instant(successMessage),
            type: 'success',
            acceptText: this.translate.instant('OK'),
          })
          .subscribe({
            accept: () => resolve(),
            close: () => resolve(),
          });
      });
    } else {
      this.alertService.open({
        title: this.translate.instant('Error'),
        message: this.translate.instant(errorMessage),
        type: 'error',
        acceptText: this.translate.instant('OK'),
      });
    }
  }

  async handleReject(quotation: IQuotation): Promise<boolean> {
    const confirmed = await this.confirmAction(
      'You won not be able to revert this change!',
    );

    if (!confirmed) return false;

    this.spinnerService.show();
    const userId = this.cookieAuthService.getUserId!;

    try {
      const success = await firstValueFrom(
        this.quotationService.updateStatus(
          quotation.id_factura!,
          true,
          false,
          '',
          false,
          '',
          false,
          parseInt(userId),
        ),
      );

      await this.showOperationResult(success);

      this.spinnerService.hide();

      return success;
    } catch {
      await this.showOperationResult(false);
      this.spinnerService.hide();
      return false;
    }
  }

  async handleDelivery(quotation: IQuotation): Promise<void> {
    const isProspective =
      parseInt(quotation.cliente.id_customerType!.toString()) ===
      QuotationState.DELIVERY;

    const dialogRef = this.dialog.open(DialogComponent, {
      data: {
        deliveryData: {
          id_quotation: quotation.id_factura!,
          id_customer: quotation.cliente.id_cliente,
          isProspective: isProspective,
        },
      },
    });

    await firstValueFrom(dialogRef.afterClosed());

    this.actionCompletedSource.next('delivery');
  }

  async handleInvoice(quotation: IQuotation): Promise<boolean> {
    const isProspective =
      parseInt(quotation.cliente.id_customerType!.toString()) ===
      QuotationState.DELIVERY;

    if (isProspective) {
      this.alertService.open({
        title: this.translate.instant('Error'),
        message: this.translate.instant(
          'You cannot change the status because the customer is a prospect!',
        ),
        type: 'error',
        acceptText: this.translate.instant('OK'),
      });
      return false;
    }

    const confirmed = await this.confirmAction(
      'You won not be able to revert this change!',
    );

    if (!confirmed) return false;

    this.spinnerService.show();
    const userId = this.cookieAuthService.getUserId!;
    const invoiceDate = new Date().toISOString();

    try {
      const success = await firstValueFrom(
        this.quotationService.updateStatus(
          quotation.id_factura!,
          false,
          false,
          '',
          true,
          invoiceDate,
          false,
          parseInt(userId),
        ),
      );

      await this.showOperationResult(success);

      this.spinnerService.hide();

      return success;
    } catch {
      await this.showOperationResult(false);
      this.spinnerService.hide();
      return false;
    }
  }

  async handleSendDian(quotation: IQuotation): Promise<boolean> {
    if (this.companyFeaturesService.isBillyCompany()) {
      try {
        await this.billyInvoiceService.sendDian(
          parseInt(quotation.id_factura!.toString()),
        );
        return true;
      } catch {
        return false;
      }
    } else if (this.companyFeaturesService.isInternalInvoicingCompany()) {
      try {
        await this.dianInvoiceService.sendDian(quotation.id_factura!);
        return true;
      } catch {
        return false;
      }
    } else {
      this.alertService.open({
        title: this.translate.instant('Warning'),
        message: this.translate.instant(
          'This company is not configured to send invoices to DIAN, or is not available in your country.',
        ),
        type: 'warning',
        acceptText: this.translate.instant('OK'),
      });
      return false;
    }
  }

  async handleEdit(quotation: IQuotation): Promise<void> {
    const state = quotation.estado!;

    if (state === QuotationState.ORDER || state === QuotationState.DELIVERY) {
      const dialogRef = this.dialog.open(DialogComponent, {
        data: { id_factura: quotation.id_factura },
      });

      await firstValueFrom(dialogRef.afterClosed());

      this.actionCompletedSource.next('edit');
    } else {
      this.alertService.open({
        title: this.translate.instant('Warning'),
        message: this.translate.instant(
          'You cannot edit a quote in this state.',
        ),
        type: 'warning',
        acceptText: this.translate.instant('OK'),
      });
    }
  }

  handleCreditNote(quotation: IQuotation): void {
    this.modalService
      .openComponent(
        NotesComponent,
        {
          variant: ModalVariant.DIALOG,
        },
        {
          invoiceId: parseInt(quotation.id_factura?.toString() ?? '0'),
          noteType: 'credit',
        },
      )
      .subscribe(() => {
        this.actionCompletedSource.next('credit-note');
      });
  }

  handleDebitNote(quotation: IQuotation): void {
    this.modalService
      .openComponent(
        NotesComponent,
        {
          variant: ModalVariant.DIALOG,
        },
        {
          invoiceId: parseInt(quotation.id_factura?.toString() ?? '0'),
          noteType: 'debit',
        },
      )
      .subscribe(() => {
        this.actionCompletedSource.next('debit-note');
      });
  }

  async handleRegister(): Promise<void> {
    const dialogRef = this.dialog.open(DialogComponent, {
      data: { register: true },
    });

    await firstValueFrom(dialogRef.afterClosed());
    this.actionCompletedSource.next('register');
  }

  viewPdf(quotation: IQuotation): void {
    const companyObject = this.cookieAuthService.getCompanyObject;
    const companyId = companyObject?.Id_company?.toString();

    if (companyId && quotation.id_factura) {
      this.router.navigate(['/details', quotation.id_factura, companyId, 1]);
    }
  }

  viewPdfLetterhead(quotation: IQuotation): void {
    const companyObject = this.cookieAuthService.getCompanyObject;
    const companyId = companyObject?.Id_company?.toString();

    if (companyId && quotation.id_factura) {
      this.router.navigate(['/details', quotation.id_factura, companyId, 2]);
    }
  }

  async handleAction(actionId: string, quotation: IQuotation): Promise<void> {
    switch (actionId) {
      case 'view-pdf':
        this.viewPdf(quotation);
        break;

      case 'view-pdf-letterhead':
        this.viewPdfLetterhead(quotation);
        break;

      case 'edit':
        await this.handleEdit(quotation);
        break;

      case 'send-dian': {
        const success = await this.handleSendDian(quotation);
        if (success) {
          this.actionCompletedSource.next(actionId);
        }
        break;
      }

      case 'reject': {
        const success = await this.handleReject(quotation);
        if (success) {
          this.actionCompletedSource.next(actionId);
        }
        break;
      }

      case 'delivery':
        await this.handleDelivery(quotation);
        break;

      case 'invoice': {
        const success = await this.handleInvoice(quotation);
        if (success) {
          this.actionCompletedSource.next(actionId);
        }
        break;
      }

      case 'credit-note':
        await this.handleCreditNote(quotation);
        break;

      case 'debit-note':
        await this.handleDebitNote(quotation);
        break;
    }
  }

  // ============================================================================
  // FUTURE: Portfolio features (cardType == 2)
  // ============================================================================
  // - generatePaymentReport(): Generate PDF receipt
  // - handleConfigurePayments(): Open payment dialog
  // - getInvoicePayments(): Get payment details
  // - calculateTotalPaid(): Calculate total from payments
  // ============================================================================
}
